import { createBuilder, createSchema, boolean, json, number, relationships, string, table, enumeration, } from "@rocicorp/zero";
import { functionAssignments, peopleTable } from "./equipment-plan.js";
export const projectStatuses = [
    "initial_contact",
    "offer",
    "confirmed",
    "rejected",
];
export const projectTypes = [
    "live",
    "film",
    "gfx_tracking",
    "consultancy",
];
export const companyTypes = ["customer", "provider", "main_company"];
export const projectGroupStatuses = ["active", "archived"];
export const productionModes = ["remote", "on_site"];
export const vatRates = ["CH_8_1", "NONE"];
export const languages = ["de", "en", "fr"];
export const taskStates = ["backlog", "todo", "in_progress", "done"];
export const notificationTypes = [
    "comment_mention",
    "meeting_note_mention",
    "task_assigned",
    "task_updated",
    "task_due_soon",
    "task_due_today",
    "invoice_payment_overdue",
];
export const lineItemTypes = ["generic", "equipment", "crew"];
export const quoteVersionStatuses = [
    "draft",
    "sent",
    "accepted",
    "rejected",
];
export const invoiceStatuses = ["draft", "sent", "paid", "cancelled"];
export const invoiceCurrencies = ["CHF", "EUR"];
export const awsResourceTypes = [
    "mediaconnect_flow",
    "mediaconnect_output",
    "medialive_channel",
    "medialive_input",
    "medialive_input_device",
];
export const projects = table("projects")
    .columns({
    id: string(),
    name: string(),
    number: string().optional(),
    customerID: string().from("customer_id").optional(),
    contactID: string().from("contact_id").optional(),
    previousEditionProjectID: string()
        .from("previous_edition_project_id")
        .optional(),
    projectGroupID: string().from("project_group_id").optional(),
    studioID: string().from("studio_id").optional(),
    accountManagerID: string().from("account_manager_id").optional(),
    projectManagerID: string().from("project_manager_id").optional(),
    technicalResponsibleID: string()
        .from("technical_responsible_id")
        .optional(),
    equipmentResponsibleID: string()
        .from("equipment_responsible_id")
        .optional(),
    status: enumeration().optional(),
    types: json(),
    startDate: number().from("start_date").optional(),
    endDate: number().from("end_date").optional(),
    eventStartDate: number().from("event_start_date").optional(),
    eventEndDate: number().from("event_end_date").optional(),
    planningStartDate: number().from("planning_start_date").optional(),
    planningEndDate: number().from("planning_end_date").optional(),
    notes: string().optional(),
    conditions: string().optional(),
    language: enumeration(),
    defaultVatRate: enumeration().from("default_vat_rate"),
    defaultDiscountPercent: number().from("default_discount_percent"),
    crewListRemarks: string().from("crew_list_remarks").optional(),
    mealsRemarks: string().from("meals_remarks").optional(),
    hotelsRemarks: string().from("hotels_remarks").optional(),
    productionLocation: string().from("production_location").optional(),
    productionMode: enumeration()
        .from("production_mode")
        .optional(),
    rentmanID: number().from("rentman_id").optional(),
    clockifyProjectID: string().from("clockify_project_id").optional(),
    clockifySyncedAt: number().from("clockify_synced_at").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
    deletedAt: number().from("deleted_at").optional(),
    version: number().optional(),
})
    .primaryKey("id");
const companies = table("companies")
    .columns({
    id: string(),
    name: string(),
    number: string().optional(),
    invoiceAddress: string().from("invoice_address").optional(),
    types: json(),
    isMainCompany: boolean().from("is_main_company").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
    language: string().optional(),
    deletedAt: number().from("deleted_at").optional(),
    version: number().optional(),
    rentmanID: number().from("rentman_id").optional(),
})
    .primaryKey("id");
const projectGroups = table("projectGroups")
    .from("project_groups")
    .columns({
    id: string(),
    name: string(),
    number: string().optional(),
    customerID: string().from("customer_id").optional(),
    contactID: string().from("contact_id").optional(),
    description: string().optional(),
    status: enumeration(),
    color: string().optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
    deletedAt: number().from("deleted_at").optional(),
    clockifyProjectID: string().from("clockify_project_id").optional(),
    clockifySyncedAt: number().from("clockify_synced_at").optional(),
    version: number().optional(),
})
    .primaryKey("id");
const projectCollaborators = table("projectCollaborators")
    .from("project_collaborators")
    .columns({
    id: string(),
    projectID: string().from("project_id"),
    personID: string().from("person_id"),
    canEdit: boolean().from("can_edit").optional(),
    createdAt: number().from("created_at").optional(),
})
    .primaryKey("id");
const projectAvailability = table("projectAvailability")
    .from("project_availability")
    .columns({
    id: string(),
    projectID: string().from("project_id"),
    personID: string().from("person_id"),
    status: enumeration(),
    availableDays: json().from("available_days"),
    notes: string().optional(),
    crewListRemark: string().from("crew_list_remark").optional(),
    unityUsername: string().from("unity_username").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const projectContacts = table("projectContacts")
    .from("project_contacts")
    .columns({
    id: string(),
    projectID: string().from("project_id"),
    personID: string().from("person_id"),
    description: string(),
    createdAt: number().from("created_at").optional(),
})
    .primaryKey("id");
const crewEmails = table("crewEmails")
    .from("crew_emails")
    .columns({
    id: string(),
    projectID: string().from("project_id"),
    subject: string(),
    htmlBody: string().from("html_body"),
    sentBy: string().from("sent_by").optional(),
    status: enumeration(),
    draftPersonIds: json().from("draft_person_ids"),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const crewEmailRecipients = table("crewEmailRecipients")
    .from("crew_email_recipients")
    .columns({
    id: string(),
    crewEmailID: string().from("crew_email_id"),
    personID: string().from("person_id"),
    email: string(),
    sentAt: number().from("sent_at").optional(),
})
    .primaryKey("id");
const crewEmailAttachments = table("crewEmailAttachments")
    .from("crew_email_attachments")
    .columns({
    id: string(),
    crewEmailID: string().from("crew_email_id"),
    filename: string(),
    contentType: string().from("content_type"),
    sizeBytes: number().from("size_bytes"),
})
    .primaryKey("id");
const projectMealPlans = table("projectMealPlans")
    .from("project_meal_plans")
    .columns({
    id: string(),
    projectID: string().from("project_id"),
    personID: string().from("person_id"),
    planDate: number().from("plan_date"),
    lunch: boolean(),
    dinner: boolean(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const projectHotelPlans = table("projectHotelPlans")
    .from("project_hotel_plans")
    .columns({
    id: string(),
    projectID: string().from("project_id"),
    personID: string().from("person_id"),
    planDate: number().from("plan_date"),
    needsHotel: boolean().from("needs_hotel"),
    roomType: string().from("room_type"),
    roommatePersonID: string().from("roommate_person_id").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const projectIntercomChannels = table("projectIntercomChannels")
    .from("project_intercom_channels")
    .columns({
    id: string(),
    projectID: string().from("project_id"),
    name: string(),
    description: string().optional(),
    channelNumber: number().from("channel_number").optional(),
    sortOrder: number().from("sort_order"),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const projectIntercomAssignments = table("projectIntercomAssignments")
    .from("project_intercom_assignments")
    .columns({
    id: string(),
    projectID: string().from("project_id"),
    personID: string().from("person_id").optional(),
    profileID: string().from("profile_id").optional(),
    unityUsername: string().from("unity_username").optional(),
    displayName: string().from("display_name").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const projectIntercomProfiles = table("projectIntercomProfiles")
    .from("project_intercom_profiles")
    .columns({
    id: string(),
    projectID: string().from("project_id"),
    name: string(),
    channelConfig: json().from("channel_config"),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const networkCoverageSessions = table("networkCoverageSessions")
    .from("network_coverage_sessions")
    .columns({
    id: string(),
    projectID: string().from("project_id"),
    externalSessionID: string().from("external_session_id"),
    deviceName: string().from("device_name").optional(),
    deviceUID: string().from("device_uid").optional(),
    startedAt: number().from("started_at"),
    endedAt: number().from("ended_at").optional(),
    hasGpx: boolean().from("has_gpx"),
    summaryStats: json().from("summary_stats").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const publicHolidays = table("publicHolidays")
    .from("public_holidays")
    .columns({
    id: string(),
    name: string(),
    date: number(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const employeeAbsences = table("employeeAbsences")
    .from("employee_absences")
    .columns({
    id: string(),
    personID: string().from("person_id"),
    absenceType: enumeration().from("absence_type"),
    startDate: number().from("start_date"),
    endDate: number().from("end_date"),
    notes: string().optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const employeeEntitlements = table("employeeEntitlements")
    .from("employee_entitlements")
    .columns({
    id: string(),
    personID: string().from("person_id"),
    year: number(),
    vacationDays: number().from("vacation_days"),
    carryOverDays: number().from("carry_over_days"),
    notes: string().optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const employmentPeriods = table("employmentPeriods")
    .from("employment_periods")
    .columns({
    id: string(),
    personID: string().from("person_id"),
    percentage: number(),
    validFrom: number().from("valid_from"),
    validTo: number().from("valid_to").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const overtimePayouts = table("overtimePayouts")
    .from("overtime_payouts")
    .columns({
    id: string(),
    personID: string().from("person_id"),
    date: number(),
    hours: number(),
    notes: string().optional(),
    createdBy: string().from("created_by").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const appSettings = table("appSettings")
    .from("app_settings")
    .columns({
    key: string(),
    value: json(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("key");
const allergyTags = table("allergyTags")
    .from("allergy_tags")
    .columns({
    id: string(),
    name: string(),
    createdAt: number().from("created_at").optional(),
})
    .primaryKey("id");
const personAllergyTags = table("personAllergyTags")
    .from("person_allergy_tags")
    .columns({
    id: string(),
    personID: string().from("person_id"),
    allergyTagID: string().from("allergy_tag_id"),
    createdAt: number().from("created_at").optional(),
})
    .primaryKey("id");
const studios = table("studios")
    .columns({
    id: string(),
    number: string().optional(),
    name: string(),
    description: string().optional(),
    bridgeURL: string().from("bridge_url").optional(),
    bridgeAPIKey: string().from("bridge_api_key").optional(),
    location: string().optional(),
    unityHostname: string().from("unity_hostname").optional(),
    activeConfigID: string().from("active_config_id").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
    deletedAt: number().from("deleted_at").optional(),
    version: number().optional(),
})
    .primaryKey("id");
const studioConfigs = table("studioConfigs")
    .from("studio_configs")
    .columns({
    id: string(),
    studioID: string().from("studio_id"),
    projectID: string().from("project_id").optional(),
    name: string(),
    description: string().optional(),
    version: number(),
    configData: json().from("config_data"),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const studioConnections = table("studioConnections")
    .from("studio_connections")
    .columns({
    id: string(),
    studioID: string().from("studio_id"),
    name: string(),
    connectionType: string().from("connection_type"),
    host: string(),
    port: number().optional(),
    model: string().optional(),
    managementURL: string().from("management_url").optional(),
    enabled: boolean(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const studioConfigVersions = table("studioConfigVersions")
    .from("studio_config_versions")
    .columns({
    id: string(),
    configID: string().from("config_id"),
    version: number(),
    configData: json().from("config_data"),
    createdAt: number().from("created_at").optional(),
})
    .primaryKey("id");
const brokers = table("brokers")
    .columns({
    id: string(),
    studioID: string().from("studio_id"),
    name: string(),
    brokerType: string().from("broker_type"),
    config: json(),
    enabled: boolean(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const brokerOutputs = table("brokerOutputs")
    .from("broker_outputs")
    .columns({
    id: string(),
    brokerID: string().from("broker_id"),
    name: string(),
    outputType: string().from("output_type"),
    config: json(),
    enabled: boolean(),
    sortOrder: number().from("sort_order"),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const profiles = table("profiles")
    .columns({
    id: string(),
    name: string(),
    videoCodec: string().from("video_codec").optional(),
    videoWidth: number().from("video_width").optional(),
    videoHeight: number().from("video_height").optional(),
    videoBitrate: number().from("video_bitrate").optional(),
    videoFramerate: string().from("video_framerate").optional(),
    audioCodec: string().from("audio_codec").optional(),
    audioBitrate: number().from("audio_bitrate").optional(),
    audioSampleRate: number().from("audio_sample_rate").optional(),
    config: json(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const streams = table("streams")
    .columns({
    id: string(),
    projectID: string().from("project_id"),
    name: string(),
    encoderType: string().from("encoder_type"),
    encoderConfig: json().from("encoder_config"),
    profileID: string().from("profile_id").optional(),
    brokerID: string().from("broker_id").optional(),
    enabled: boolean(),
    lifecycleState: string().from("lifecycle_state"),
    lifecycleError: string().from("lifecycle_error").optional(),
    provisionedResources: json().from("provisioned_resources").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const streamAudioPairs = table("streamAudioPairs")
    .from("stream_audio_pairs")
    .columns({
    id: string(),
    streamID: string().from("stream_id"),
    pairIndex: number().from("pair_index"),
    name: string(),
    sourceConfig: json().from("source_config"),
    createdAt: number().from("created_at").optional(),
})
    .primaryKey("id");
const streamOutputs = table("streamOutputs")
    .from("stream_outputs")
    .columns({
    id: string(),
    streamID: string().from("stream_id"),
    name: string(),
    outputType: string().from("output_type"),
    config: json(),
    enabled: boolean(),
    sortOrder: number().from("sort_order"),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const streamAudioFeeds = table("streamAudioFeeds")
    .from("stream_audio_feeds")
    .columns({
    id: string(),
    projectID: string().from("project_id"),
    name: string(),
    sortOrder: number().from("sort_order"),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const streamVideoFeeds = table("streamVideoFeeds")
    .from("stream_video_feeds")
    .columns({
    id: string(),
    projectID: string().from("project_id"),
    name: string(),
    description: string(),
    sortOrder: number().from("sort_order"),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const streamSignals = table("streamSignals")
    .from("stream_signals")
    .columns({
    id: string(),
    projectID: string().from("project_id"),
    number: number(),
    name: string(),
    description: string(),
    sortOrder: number().from("sort_order"),
    videoFeedID: string().from("video_feed_id").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const streamSignalAudioFeeds = table("streamSignalAudioFeeds")
    .from("stream_signal_audio_feeds")
    .columns({
    id: string(),
    signalID: string().from("signal_id"),
    audioFeedID: string().from("audio_feed_id"),
    sortOrder: number().from("sort_order"),
})
    .primaryKey("id");
const streamTakers = table("streamTakers")
    .from("stream_takers")
    .columns({
    id: string(),
    projectID: string().from("project_id"),
    name: string(),
    deliveryMethod: string().from("delivery_method"),
    url: string(),
    signalID: string().from("signal_id").optional(),
    contact: string(),
    notes: string(),
    testDate: number().from("test_date").optional(),
    tested: boolean(),
    preTestDone: boolean().from("pre_test_done"),
    onDayTestDone: boolean().from("on_day_test_done"),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const streamTakerAudioPairs = table("streamTakerAudioPairs")
    .from("stream_taker_audio_pairs")
    .columns({
    id: string(),
    streamTakerID: string().from("stream_taker_id"),
    pairNumber: number().from("pair_number"),
    audioFeedID: string().from("audio_feed_id").optional(),
})
    .primaryKey("id");
const streamTakerContacts = table("streamTakerContacts")
    .from("stream_taker_contacts")
    .columns({
    id: string(),
    streamTakerID: string().from("stream_taker_id"),
    personID: string().from("person_id"),
    description: string(),
})
    .primaryKey("id");
const streamTakerAudioPairTests = table("streamTakerAudioPairTests")
    .from("stream_taker_audio_pair_tests")
    .columns({
    id: string(),
    streamTakerID: string().from("stream_taker_id"),
    signalAudioFeedID: string().from("signal_audio_feed_id"),
    tested: boolean(),
    preTestDone: boolean().from("pre_test_done"),
    onDayTestDone: boolean().from("on_day_test_done"),
})
    .primaryKey("id");
const streamBrokers = table("streamBrokers")
    .from("stream_brokers")
    .columns({
    id: string(),
    streamID: string().from("stream_id"),
    brokerID: string().from("broker_id"),
    enabled: boolean(),
    sortOrder: number().from("sort_order"),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const projectAwsResourceLinks = table("projectAwsResourceLinks")
    .from("project_aws_resource_links")
    .columns({
    id: string(),
    projectID: string().from("project_id"),
    resourceType: enumeration().from("resource_type"),
    resourceID: string().from("resource_id").optional(),
    resourceArn: string().from("resource_arn").optional(),
    resourceName: string().from("resource_name").optional(),
    metadata: json(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const defaultFunctionGroups = table("defaultFunctionGroups")
    .from("default_function_groups")
    .columns({
    id: string(),
    name: string(),
    locationType: enumeration()
        .from("location_type")
        .optional(),
    displayOrder: number().from("display_order"),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const defaultFunctions = table("defaultFunctions")
    .from("default_functions")
    .columns({
    id: string(),
    name: string(),
    defaultFunctionGroupID: string()
        .from("default_function_group_id")
        .optional(),
    displayOrder: number().from("display_order"),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
    systemTag: string().from("system_tag").optional(),
    locationType: enumeration()
        .from("location_type")
        .optional(),
    showOnInitialize: boolean().from("show_on_initialize"),
})
    .primaryKey("id");
const projectFunctionGroups = table("projectFunctionGroups")
    .from("project_function_groups")
    .columns({
    id: string(),
    projectID: string().from("project_id"),
    name: string(),
    locationType: enumeration()
        .from("location_type")
        .optional(),
    displayOrder: number().from("display_order"),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const personSkillRatings = table("personSkillRatings")
    .from("person_skill_ratings")
    .columns({
    id: string(),
    personID: string().from("person_id"),
    defaultFunctionID: string().from("default_function_id"),
    rating: number(),
    notes: string().optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const projectFunctionSeries = table("projectFunctionSeries")
    .from("project_function_series")
    .columns({
    id: string(),
    projectID: string().from("project_id"),
    projectFunctionGroupID: string()
        .from("project_function_group_id")
        .optional(),
    name: string(),
    requiredCount: number().from("required_count"),
    selectedDays: json().from("selected_days"),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
    systemTag: string().from("system_tag").optional(),
    locationType: enumeration()
        .from("location_type")
        .optional(),
})
    .primaryKey("id");
const projectPersonTravelDays = table("projectPersonTravelDays")
    .from("project_person_travel_days")
    .columns({
    id: string(),
    projectID: string().from("project_id"),
    personID: string().from("person_id"),
    travelDate: number().from("travel_date"),
    remark: string().optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const projectFunctions = table("projectFunctions")
    .from("project_functions")
    .columns({
    id: string(),
    projectID: string().from("project_id"),
    seriesID: string().from("series_id").optional(),
    functionDate: number().from("function_date"),
    name: string(),
    requiredCount: number().from("required_count"),
    requiredCountOverride: number().from("required_count_override").optional(),
    startTime: number().from("start_time").optional(),
    endTime: number().from("end_time").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
    rentmanID: number().from("rentman_id").optional(),
})
    .primaryKey("id");
const tasks = table("tasks")
    .columns({
    id: string(),
    title: string(),
    description: string().optional(),
    state: enumeration(),
    dueDate: number().from("due_date").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const taskAssignees = table("taskAssignees")
    .from("task_assignees")
    .columns({
    id: string(),
    taskID: string().from("task_id"),
    personID: string().from("person_id"),
    createdAt: number().from("created_at").optional(),
})
    .primaryKey("id");
const taskProjects = table("taskProjects")
    .from("task_projects")
    .columns({
    id: string(),
    taskID: string().from("task_id"),
    projectID: string().from("project_id"),
    createdAt: number().from("created_at").optional(),
})
    .primaryKey("id");
const taskProjectGroups = table("taskProjectGroups")
    .from("task_project_groups")
    .columns({
    id: string(),
    taskID: string().from("task_id"),
    projectGroupID: string().from("project_group_id"),
    createdAt: number().from("created_at").optional(),
})
    .primaryKey("id");
const notifications = table("notifications")
    .columns({
    id: string(),
    userID: string().from("user_id"),
    type: enumeration(),
    title: string(),
    body: string().optional(),
    entityType: string().from("entity_type").optional(),
    entityID: string().from("entity_id").optional(),
    sourceCommentID: string().from("source_comment_id").optional(),
    sourceMeetingNoteID: string().from("source_meeting_note_id").optional(),
    sourceMentionID: string().from("source_mention_id").optional(),
    sourceTaskID: string().from("source_task_id").optional(),
    actorID: string().from("actor_id").optional(),
    isRead: boolean().from("is_read"),
    createdAt: number().from("created_at").optional(),
})
    .primaryKey("id");
const comments = table("comments")
    .columns({
    id: string(),
    entityType: string().from("entity_type"),
    entityID: string().from("entity_id"),
    parentID: string().from("parent_id").optional(),
    content: string(),
    authorID: string().from("author_id"),
    isResolved: boolean().from("is_resolved"),
    resolvedBy: string().from("resolved_by").optional(),
    resolvedAt: number().from("resolved_at").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const commentMentions = table("commentMentions")
    .from("comment_mentions")
    .columns({
    id: string(),
    commentID: string().from("comment_id"),
    personID: string().from("person_id"),
    createdAt: number().from("created_at").optional(),
})
    .primaryKey("id");
const documents = table("documents")
    .columns({
    id: string(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const studioDocuments = table("studioDocuments")
    .from("studio_documents")
    .columns({
    id: string(),
    title: string(),
    studioID: string().from("studio_id"),
    documentID: string().from("document_id").optional(),
    createdBy: string().from("created_by").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const globalDocuments = table("globalDocuments")
    .from("global_documents")
    .columns({
    id: string(),
    title: string(),
    parentID: string().from("parent_id").optional(),
    isFolder: boolean().from("is_folder"),
    hiddenFromDocs: boolean().from("hidden_from_docs"),
    documentID: string().from("document_id").optional(),
    sortOrder: number().from("sort_order"),
    createdBy: string().from("created_by").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const globalDocumentLinks = table("globalDocumentLinks")
    .from("global_document_links")
    .columns({
    id: string(),
    globalDocumentID: string().from("global_document_id"),
    linkType: string().from("link_type"),
    linkID: string().from("link_id"),
    createdAt: number().from("created_at").optional(),
})
    .primaryKey("id");
const meetingNoteProjectMentions = table("meetingNoteProjectMentions")
    .from("meeting_note_project_mentions")
    .columns({
    id: string(),
    meetingNoteID: string().from("meeting_note_id"),
    projectID: string().from("project_id"),
    sourceMentionID: string().from("source_mention_id").optional(),
    createdAt: number().from("created_at").optional(),
})
    .primaryKey("id");
const meetingNoteTags = table("meetingNoteTags")
    .from("meeting_note_tags")
    .columns({
    id: string(),
    name: string(),
    color: string().optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const meetingNotes = table("meetingNotes")
    .from("meeting_notes")
    .columns({
    id: string(),
    title: string(),
    content: string().optional(),
    meetingDate: number().from("meeting_date"),
    projectID: string().from("project_id").optional(),
    projectGroupID: string().from("project_group_id").optional(),
    documentID: string().from("document_id").optional(),
    createdBy: string().from("created_by").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const meetingNoteTagAssignments = table("meetingNoteTagAssignments")
    .from("meeting_note_tag_assignments")
    .columns({
    id: string(),
    meetingNoteID: string().from("meeting_note_id"),
    tagID: string().from("tag_id"),
    createdAt: number().from("created_at").optional(),
})
    .primaryKey("id");
const meetingNoteMentions = table("meetingNoteMentions")
    .from("meeting_note_mentions")
    .columns({
    id: string(),
    meetingNoteID: string().from("meeting_note_id"),
    personID: string().from("person_id"),
    createdAt: number().from("created_at").optional(),
})
    .primaryKey("id");
const quoteLineItemTemplates = table("quoteLineItemTemplates")
    .from("quote_line_item_templates")
    .columns({
    id: string(),
    name: json(),
    code: string().optional(),
    defaultAmount: number().from("default_amount"),
    defaultUnitPrice: number().from("default_unit_price"),
    lineItemType: enumeration().from("line_item_type"),
    category: string().optional(),
    sortOrder: number().from("sort_order"),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const quoteGroupTemplates = table("quoteGroupTemplates")
    .from("quote_group_templates")
    .columns({
    id: string(),
    name: json(),
    defaultIsOptional: boolean().from("default_is_optional"),
    defaultApplyProjectDiscount: boolean().from("default_apply_project_discount"),
    category: string().optional(),
    sortOrder: number().from("sort_order"),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const quoteGroupTemplateItems = table("quoteGroupTemplateItems")
    .from("quote_group_template_items")
    .columns({
    id: string(),
    groupTemplateID: string().from("group_template_id"),
    lineItemTemplateID: string().from("line_item_template_id").optional(),
    isTitle: boolean().from("is_title"),
    name: json().optional(),
    defaultAmount: number().from("default_amount"),
    sortOrder: number().from("sort_order"),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const clockifyTimeEntries = table("clockifyTimeEntries")
    .from("clockify_time_entries")
    .columns({
    id: string(),
    clockifyID: string().from("clockify_id"),
    projectID: string().from("project_id").optional(),
    projectGroupID: string().from("project_group_id").optional(),
    personID: string().from("person_id").optional(),
    clockifyUserID: string().from("clockify_user_id"),
    description: string().optional(),
    startTime: number().from("start_time"),
    endTime: number().from("end_time").optional(),
    durationSeconds: number().from("duration_seconds").optional(),
    billable: boolean(),
    syncedAt: number().from("synced_at").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const quotes = table("quotes")
    .columns({
    id: string(),
    projectID: string().from("project_id"),
    name: string(),
    quoteNumber: number().from("quote_number").optional(),
    customerID: string().from("customer_id").optional(),
    contactID: string().from("contact_id").optional(),
    rentmanID: number().from("rentman_id").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const quoteVersions = table("quoteVersions")
    .from("quote_versions")
    .columns({
    id: string(),
    quoteID: string().from("quote_id"),
    versionLabel: string().from("version_label"),
    versionNumber: number().from("version_number"),
    internalName: string().from("internal_name").optional(),
    quoteDate: number().from("quote_date").optional(),
    validUntil: number().from("valid_until").optional(),
    validityDays: number().from("validity_days"),
    isAccepted: boolean().from("is_accepted"),
    pdfURL: string().from("pdf_url").optional(),
    projectDiscountPercent: number().from("project_discount_percent"),
    vatRate: enumeration().from("vat_rate"),
    content: json(),
    deletedAt: number().from("deleted_at").optional(),
    status: enumeration(),
    generalConditions: string().from("general_conditions").optional(),
    rentmanID: number().from("rentman_id").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const quoteGroups = table("quoteGroups")
    .from("quote_groups")
    .columns({
    id: string(),
    quoteVersionID: string().from("quote_version_id"),
    name: string(),
    displayOrder: number().from("display_order"),
    isOptional: boolean().from("is_optional"),
    applyProjectDiscount: boolean().from("apply_project_discount"),
    rentmanID: number().from("rentman_id").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const quoteLineItems = table("quoteLineItems")
    .from("quote_line_items")
    .columns({
    id: string(),
    quoteGroupID: string().from("quote_group_id"),
    name: string(),
    amount: number(),
    factor: number(),
    unitPrice: number().from("unit_price"),
    discount: number(),
    notes: string(),
    displayOrder: number().from("display_order"),
    rentmanID: number().from("rentman_id").optional(),
    lineItemType: enumeration().from("line_item_type"),
    isTitle: boolean().from("is_title"),
    externalNote: string().from("external_note"),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const quoteDeductionItems = table("quoteDeductionItems")
    .from("quote_deduction_items")
    .columns({
    id: string(),
    quoteVersionID: string().from("quote_version_id"),
    description: string(),
    amount: number(),
    displayOrder: number().from("display_order"),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const invoices = table("invoices")
    .columns({
    id: string(),
    projectID: string().from("project_id").optional(),
    sourceQuoteVersionID: string().from("source_quote_version_id").optional(),
    customerID: string().from("customer_id").optional(),
    contactID: string().from("contact_id").optional(),
    invoiceNumber: number().from("invoice_number").optional(),
    title: string(),
    language: enumeration().optional(),
    currency: enumeration(),
    status: enumeration(),
    invoiceDate: number().from("invoice_date").optional(),
    dueDate: number().from("due_date").optional(),
    paymentTermDays: number().from("payment_term_days"),
    projectDiscountPercent: number().from("project_discount_percent"),
    vatRate: enumeration().from("vat_rate"),
    depositPercent: number().from("deposit_percent").optional(),
    qrReference: string().from("qr_reference").optional(),
    includeQRBill: boolean().from("include_qr_bill"),
    pdfURL: string().from("pdf_url").optional(),
    notes: string().optional(),
    totalNet: number().from("total_net"),
    totalGross: number().from("total_gross"),
    createdBy: string().from("created_by").optional(),
    deletedAt: number().from("deleted_at").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const invoiceReminders = table("invoiceReminders")
    .from("invoice_reminders")
    .columns({
    id: string(),
    invoiceID: string().from("invoice_id"),
    reminderNumber: number().from("reminder_number"),
    title: string(),
    reminderDate: number().from("reminder_date"),
    dueDate: number().from("due_date"),
    paymentTermDays: number().from("payment_term_days"),
    pdfURL: string().from("pdf_url").optional(),
    status: enumeration(),
    totalNet: number().from("total_net"),
    totalGross: number().from("total_gross"),
    createdBy: string().from("created_by").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const invoiceGroups = table("invoiceGroups")
    .from("invoice_groups")
    .columns({
    id: string(),
    invoiceID: string().from("invoice_id"),
    name: string(),
    displayOrder: number().from("display_order"),
    isOptional: boolean().from("is_optional"),
    applyProjectDiscount: boolean().from("apply_project_discount"),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const invoiceLineItems = table("invoiceLineItems")
    .from("invoice_line_items")
    .columns({
    id: string(),
    invoiceGroupID: string().from("invoice_group_id"),
    name: string(),
    amount: number(),
    factor: number(),
    unitPrice: number().from("unit_price"),
    discount: number(),
    notes: string(),
    externalNote: string().from("external_note"),
    displayOrder: number().from("display_order"),
    isTitle: boolean().from("is_title"),
    lineItemType: enumeration().from("line_item_type"),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const invoiceDeductionItems = table("invoiceDeductionItems")
    .from("invoice_deduction_items")
    .columns({
    id: string(),
    invoiceID: string().from("invoice_id"),
    description: string(),
    amount: number(),
    displayOrder: number().from("display_order"),
    sourceInvoiceID: string().from("source_invoice_id").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const projectRelationships = relationships(projects, ({ one, many }) => ({
    companies: one({
        sourceField: ["customerID"],
        destSchema: companies,
        destField: ["id"],
    }),
    contact: one({
        sourceField: ["contactID"],
        destSchema: peopleTable,
        destField: ["id"],
    }),
    project_manager: one({
        sourceField: ["projectManagerID"],
        destSchema: peopleTable,
        destField: ["id"],
    }),
    account_manager: one({
        sourceField: ["accountManagerID"],
        destSchema: peopleTable,
        destField: ["id"],
    }),
    technical_responsible: one({
        sourceField: ["technicalResponsibleID"],
        destSchema: peopleTable,
        destField: ["id"],
    }),
    equipment_responsible: one({
        sourceField: ["equipmentResponsibleID"],
        destSchema: peopleTable,
        destField: ["id"],
    }),
    project_groups: one({
        sourceField: ["projectGroupID"],
        destSchema: projectGroups,
        destField: ["id"],
    }),
    previous_edition: one({
        sourceField: ["previousEditionProjectID"],
        destSchema: projects,
        destField: ["id"],
    }),
    collaborators: many({
        sourceField: ["id"],
        destSchema: projectCollaborators,
        destField: ["projectID"],
    }),
    awsResourceLinks: many({
        sourceField: ["id"],
        destSchema: projectAwsResourceLinks,
        destField: ["projectID"],
    }),
    availability: many({
        sourceField: ["id"],
        destSchema: projectAvailability,
        destField: ["projectID"],
    }),
}));
const availabilityRelationships = relationships(projectAvailability, ({ one }) => ({
    project: one({
        sourceField: ["projectID"],
        destSchema: projects,
        destField: ["id"],
    }),
    person: one({
        sourceField: ["personID"],
        destSchema: peopleTable,
        destField: ["id"],
    }),
}));
const projectContactRelationships = relationships(projectContacts, ({ one }) => ({
    person: one({
        sourceField: ["personID"],
        destSchema: peopleTable,
        destField: ["id"],
    }),
}));
const crewEmailRelationships = relationships(crewEmails, ({ many }) => ({
    recipients: many({
        sourceField: ["id"],
        destSchema: crewEmailRecipients,
        destField: ["crewEmailID"],
    }),
    attachments: many({
        sourceField: ["id"],
        destSchema: crewEmailAttachments,
        destField: ["crewEmailID"],
    }),
}));
const crewEmailRecipientRelationships = relationships(crewEmailRecipients, ({ one }) => ({
    person: one({
        sourceField: ["personID"],
        destSchema: peopleTable,
        destField: ["id"],
    }),
}));
const employeeAbsenceRelationships = relationships(employeeAbsences, ({ one }) => ({
    person: one({
        sourceField: ["personID"],
        destSchema: peopleTable,
        destField: ["id"],
    }),
}));
const personAllergyTagRelationships = relationships(personAllergyTags, ({ one }) => ({
    allergyTag: one({
        sourceField: ["allergyTagID"],
        destSchema: allergyTags,
        destField: ["id"],
    }),
}));
const intercomAssignmentRelationships = relationships(projectIntercomAssignments, ({ one }) => ({
    person: one({
        sourceField: ["personID"],
        destSchema: peopleTable,
        destField: ["id"],
    }),
    profile: one({
        sourceField: ["profileID"],
        destSchema: projectIntercomProfiles,
        destField: ["id"],
    }),
}));
const studioRelationships = relationships(studios, ({ many }) => ({
    configs: many({
        sourceField: ["id"],
        destSchema: studioConfigs,
        destField: ["studioID"],
    }),
    connections: many({
        sourceField: ["id"],
        destSchema: studioConnections,
        destField: ["studioID"],
    }),
    brokers: many({
        sourceField: ["id"],
        destSchema: brokers,
        destField: ["studioID"],
    }),
}));
const studioConnectionRelationships = relationships(studioConnections, ({ one }) => ({
    studio: one({
        sourceField: ["studioID"],
        destSchema: studios,
        destField: ["id"],
    }),
}));
const studioConfigRelationships = relationships(studioConfigs, ({ one, many }) => ({
    studio: one({
        sourceField: ["studioID"],
        destSchema: studios,
        destField: ["id"],
    }),
    versions: many({
        sourceField: ["id"],
        destSchema: studioConfigVersions,
        destField: ["configID"],
    }),
}));
const brokerRelationships = relationships(brokers, ({ many }) => ({
    outputs: many({
        sourceField: ["id"],
        destSchema: brokerOutputs,
        destField: ["brokerID"],
    }),
}));
const streamRelationships = relationships(streams, ({ one, many }) => ({
    broker: one({
        sourceField: ["brokerID"],
        destSchema: brokers,
        destField: ["id"],
    }),
    audioPairs: many({
        sourceField: ["id"],
        destSchema: streamAudioPairs,
        destField: ["streamID"],
    }),
    outputs: many({
        sourceField: ["id"],
        destSchema: streamOutputs,
        destField: ["streamID"],
    }),
    streamBrokers: many({
        sourceField: ["id"],
        destSchema: streamBrokers,
        destField: ["streamID"],
    }),
}));
const streamBrokerRelationships = relationships(streamBrokers, ({ one }) => ({
    broker: one({
        sourceField: ["brokerID"],
        destSchema: brokers,
        destField: ["id"],
    }),
}));
const projectAwsResourceLinkRelationships = relationships(projectAwsResourceLinks, ({ one }) => ({
    project: one({
        sourceField: ["projectID"],
        destSchema: projects,
        destField: ["id"],
    }),
}));
const streamSignalRelationships = relationships(streamSignals, ({ one, many }) => ({
    videoFeed: one({
        sourceField: ["videoFeedID"],
        destSchema: streamVideoFeeds,
        destField: ["id"],
    }),
    audioFeedLinks: many({
        sourceField: ["id"],
        destSchema: streamSignalAudioFeeds,
        destField: ["signalID"],
    }),
}));
const streamSignalAudioFeedRelationships = relationships(streamSignalAudioFeeds, ({ one }) => ({
    audioFeed: one({
        sourceField: ["audioFeedID"],
        destSchema: streamAudioFeeds,
        destField: ["id"],
    }),
}));
const streamTakerRelationships = relationships(streamTakers, ({ one, many }) => ({
    signal: one({
        sourceField: ["signalID"],
        destSchema: streamSignals,
        destField: ["id"],
    }),
    audioPairs: many({
        sourceField: ["id"],
        destSchema: streamTakerAudioPairs,
        destField: ["streamTakerID"],
    }),
    contacts: many({
        sourceField: ["id"],
        destSchema: streamTakerContacts,
        destField: ["streamTakerID"],
    }),
    audioPairTests: many({
        sourceField: ["id"],
        destSchema: streamTakerAudioPairTests,
        destField: ["streamTakerID"],
    }),
}));
const streamTakerAudioPairRelationships = relationships(streamTakerAudioPairs, ({ one }) => ({
    audioFeed: one({
        sourceField: ["audioFeedID"],
        destSchema: streamAudioFeeds,
        destField: ["id"],
    }),
}));
const streamTakerContactRelationships = relationships(streamTakerContacts, ({ one }) => ({
    person: one({
        sourceField: ["personID"],
        destSchema: peopleTable,
        destField: ["id"],
    }),
}));
const streamTakerAudioPairTestRelationships = relationships(streamTakerAudioPairTests, ({ one }) => ({
    signalAudioFeed: one({
        sourceField: ["signalAudioFeedID"],
        destSchema: streamSignalAudioFeeds,
        destField: ["id"],
    }),
}));
const projectGroupRelationships = relationships(projectGroups, ({ one, many }) => ({
    companies: one({
        sourceField: ["customerID"],
        destSchema: companies,
        destField: ["id"],
    }),
    contact: one({
        sourceField: ["contactID"],
        destSchema: peopleTable,
        destField: ["id"],
    }),
    projects: many({
        sourceField: ["id"],
        destSchema: projects,
        destField: ["projectGroupID"],
    }),
}));
const peopleRelationships = relationships(peopleTable, ({ one, many }) => ({
    companies: one({
        sourceField: ["companyID"],
        destSchema: companies,
        destField: ["id"],
    }),
    personAllergyTags: many({
        sourceField: ["id"],
        destSchema: personAllergyTags,
        destField: ["personID"],
    }),
}));
const skillRatingRelationships = relationships(personSkillRatings, ({ one }) => ({
    defaultFunction: one({
        sourceField: ["defaultFunctionID"],
        destSchema: defaultFunctions,
        destField: ["id"],
    }),
}));
const defaultFunctionRelationships = relationships(defaultFunctions, ({ one }) => ({
    defaultFunctionGroup: one({
        sourceField: ["defaultFunctionGroupID"],
        destSchema: defaultFunctionGroups,
        destField: ["id"],
    }),
}));
const projectFunctionSeriesRelationships = relationships(projectFunctionSeries, ({ one, many }) => ({
    projectFunctionGroup: one({
        sourceField: ["projectFunctionGroupID"],
        destSchema: projectFunctionGroups,
        destField: ["id"],
    }),
    days: many({
        sourceField: ["id"],
        destSchema: projectFunctions,
        destField: ["seriesID"],
    }),
}));
const projectPersonTravelDayRelationships = relationships(projectPersonTravelDays, ({ one }) => ({
    person: one({
        sourceField: ["personID"],
        destSchema: peopleTable,
        destField: ["id"],
    }),
}));
const projectFunctionRelationships = relationships(projectFunctions, ({ one, many }) => ({
    project: one({
        sourceField: ["projectID"],
        destSchema: projects,
        destField: ["id"],
    }),
    assignments: many({
        sourceField: ["id"],
        destSchema: functionAssignments,
        destField: ["functionID"],
    }),
}));
export const projectFunctionAssignmentRelationships = relationships(functionAssignments, ({ one }) => ({
    person: one({
        sourceField: ["personID"],
        destSchema: peopleTable,
        destField: ["id"],
    }),
    projectFunction: one({
        sourceField: ["functionID"],
        destSchema: projectFunctions,
        destField: ["id"],
    }),
}));
const taskRelationships = relationships(tasks, ({ many }) => ({
    assignees: many({
        sourceField: ["id"],
        destSchema: taskAssignees,
        destField: ["taskID"],
    }),
    projects: many({
        sourceField: ["id"],
        destSchema: taskProjects,
        destField: ["taskID"],
    }),
    projectGroups: many({
        sourceField: ["id"],
        destSchema: taskProjectGroups,
        destField: ["taskID"],
    }),
}));
const taskAssigneeRelationships = relationships(taskAssignees, ({ one }) => ({
    person: one({
        sourceField: ["personID"],
        destSchema: peopleTable,
        destField: ["id"],
    }),
}));
const taskProjectRelationships = relationships(taskProjects, ({ one }) => ({
    project: one({
        sourceField: ["projectID"],
        destSchema: projects,
        destField: ["id"],
    }),
}));
const taskProjectGroupRelationships = relationships(taskProjectGroups, ({ one }) => ({
    projectGroup: one({
        sourceField: ["projectGroupID"],
        destSchema: projectGroups,
        destField: ["id"],
    }),
}));
const commentRelationships = relationships(comments, ({ one, many }) => ({
    author: one({
        sourceField: ["authorID"],
        destSchema: peopleTable,
        destField: ["userID"],
    }),
    resolver: one({
        sourceField: ["resolvedBy"],
        destSchema: peopleTable,
        destField: ["userID"],
    }),
    mentions: many({
        sourceField: ["id"],
        destSchema: commentMentions,
        destField: ["commentID"],
    }),
}));
const commentMentionRelationships = relationships(commentMentions, ({ one }) => ({
    people: one({
        sourceField: ["personID"],
        destSchema: peopleTable,
        destField: ["id"],
    }),
}));
const meetingNoteRelationships = relationships(meetingNotes, ({ one, many }) => ({
    document: one({
        sourceField: ["documentID"],
        destSchema: documents,
        destField: ["id"],
    }),
    tagAssignments: many({
        sourceField: ["id"],
        destSchema: meetingNoteTagAssignments,
        destField: ["meetingNoteID"],
    }),
    mentions: many({
        sourceField: ["id"],
        destSchema: meetingNoteMentions,
        destField: ["meetingNoteID"],
    }),
    projectMentions: many({
        sourceField: ["id"],
        destSchema: meetingNoteProjectMentions,
        destField: ["meetingNoteID"],
    }),
    project: one({
        sourceField: ["projectID"],
        destSchema: projects,
        destField: ["id"],
    }),
    projectGroup: one({
        sourceField: ["projectGroupID"],
        destSchema: projectGroups,
        destField: ["id"],
    }),
}));
const studioDocumentRelationships = relationships(studioDocuments, ({ one }) => ({
    document: one({
        sourceField: ["documentID"],
        destSchema: documents,
        destField: ["id"],
    }),
    studio: one({
        sourceField: ["studioID"],
        destSchema: studios,
        destField: ["id"],
    }),
}));
const globalDocumentRelationships = relationships(globalDocuments, ({ one, many }) => ({
    document: one({
        sourceField: ["documentID"],
        destSchema: documents,
        destField: ["id"],
    }),
    links: many({
        sourceField: ["id"],
        destSchema: globalDocumentLinks,
        destField: ["globalDocumentID"],
    }),
}));
const meetingNoteTagAssignmentRelationships = relationships(meetingNoteTagAssignments, ({ one }) => ({
    tag: one({
        sourceField: ["tagID"],
        destSchema: meetingNoteTags,
        destField: ["id"],
    }),
}));
const meetingNoteMentionRelationships = relationships(meetingNoteMentions, ({ one }) => ({
    people: one({
        sourceField: ["personID"],
        destSchema: peopleTable,
        destField: ["id"],
    }),
}));
const meetingNoteProjectMentionRelationships = relationships(meetingNoteProjectMentions, ({ one }) => ({
    meetingNote: one({
        sourceField: ["meetingNoteID"],
        destSchema: meetingNotes,
        destField: ["id"],
    }),
    project: one({
        sourceField: ["projectID"],
        destSchema: projects,
        destField: ["id"],
    }),
}));
const quoteGroupTemplateRelationships = relationships(quoteGroupTemplates, ({ many }) => ({
    items: many({
        sourceField: ["id"],
        destSchema: quoteGroupTemplateItems,
        destField: ["groupTemplateID"],
    }),
}));
const quoteGroupTemplateItemRelationships = relationships(quoteGroupTemplateItems, ({ one }) => ({
    template: one({
        sourceField: ["lineItemTemplateID"],
        destSchema: quoteLineItemTemplates,
        destField: ["id"],
    }),
}));
const clockifyTimeEntryRelationships = relationships(clockifyTimeEntries, ({ one }) => ({
    people: one({
        sourceField: ["personID"],
        destSchema: peopleTable,
        destField: ["id"],
    }),
    projectGroup: one({
        sourceField: ["projectGroupID"],
        destSchema: projectGroups,
        destField: ["id"],
    }),
}));
const quoteRelationships = relationships(quotes, ({ one, many }) => ({
    project: one({
        sourceField: ["projectID"],
        destSchema: projects,
        destField: ["id"],
    }),
    versions: many({
        sourceField: ["id"],
        destSchema: quoteVersions,
        destField: ["quoteID"],
    }),
    customer: one({
        sourceField: ["customerID"],
        destSchema: companies,
        destField: ["id"],
    }),
    contact: one({
        sourceField: ["contactID"],
        destSchema: peopleTable,
        destField: ["id"],
    }),
}));
const quoteVersionRelationships = relationships(quoteVersions, ({ one, many }) => ({
    quote: one({
        sourceField: ["quoteID"],
        destSchema: quotes,
        destField: ["id"],
    }),
    groups: many({
        sourceField: ["id"],
        destSchema: quoteGroups,
        destField: ["quoteVersionID"],
    }),
    deductions: many({
        sourceField: ["id"],
        destSchema: quoteDeductionItems,
        destField: ["quoteVersionID"],
    }),
}));
const quoteGroupRelationships = relationships(quoteGroups, ({ one, many }) => ({
    version: one({
        sourceField: ["quoteVersionID"],
        destSchema: quoteVersions,
        destField: ["id"],
    }),
    items: many({
        sourceField: ["id"],
        destSchema: quoteLineItems,
        destField: ["quoteGroupID"],
    }),
}));
const quoteLineItemRelationships = relationships(quoteLineItems, ({ one }) => ({
    group: one({
        sourceField: ["quoteGroupID"],
        destSchema: quoteGroups,
        destField: ["id"],
    }),
}));
const quoteDeductionItemRelationships = relationships(quoteDeductionItems, ({ one }) => ({
    version: one({
        sourceField: ["quoteVersionID"],
        destSchema: quoteVersions,
        destField: ["id"],
    }),
}));
const invoiceRelationships = relationships(invoices, ({ one, many }) => ({
    project: one({
        sourceField: ["projectID"],
        destSchema: projects,
        destField: ["id"],
    }),
    sourceQuoteVersion: one({
        sourceField: ["sourceQuoteVersionID"],
        destSchema: quoteVersions,
        destField: ["id"],
    }),
    customer: one({
        sourceField: ["customerID"],
        destSchema: companies,
        destField: ["id"],
    }),
    contact: one({
        sourceField: ["contactID"],
        destSchema: peopleTable,
        destField: ["id"],
    }),
    groups: many({
        sourceField: ["id"],
        destSchema: invoiceGroups,
        destField: ["invoiceID"],
    }),
    deductions: many({
        sourceField: ["id"],
        destSchema: invoiceDeductionItems,
        destField: ["invoiceID"],
    }),
    reminders: many({
        sourceField: ["id"],
        destSchema: invoiceReminders,
        destField: ["invoiceID"],
    }),
}));
const invoiceReminderRelationships = relationships(invoiceReminders, ({ one }) => ({
    invoice: one({
        sourceField: ["invoiceID"],
        destSchema: invoices,
        destField: ["id"],
    }),
}));
const invoiceGroupRelationships = relationships(invoiceGroups, ({ one, many }) => ({
    invoice: one({
        sourceField: ["invoiceID"],
        destSchema: invoices,
        destField: ["id"],
    }),
    items: many({
        sourceField: ["id"],
        destSchema: invoiceLineItems,
        destField: ["invoiceGroupID"],
    }),
}));
const invoiceLineItemRelationships = relationships(invoiceLineItems, ({ one }) => ({
    group: one({
        sourceField: ["invoiceGroupID"],
        destSchema: invoiceGroups,
        destField: ["id"],
    }),
}));
const invoiceDeductionItemRelationships = relationships(invoiceDeductionItems, ({ one }) => ({
    invoice: one({
        sourceField: ["invoiceID"],
        destSchema: invoices,
        destField: ["id"],
    }),
    sourceInvoice: one({
        sourceField: ["sourceInvoiceID"],
        destSchema: invoices,
        destField: ["id"],
    }),
}));
export const projectTables = [
    projects,
    companies,
    projectGroups,
    projectCollaborators,
    projectAvailability,
    projectContacts,
    crewEmails,
    crewEmailRecipients,
    crewEmailAttachments,
    projectMealPlans,
    projectHotelPlans,
    projectIntercomChannels,
    projectIntercomAssignments,
    projectIntercomProfiles,
    networkCoverageSessions,
    publicHolidays,
    employeeAbsences,
    employeeEntitlements,
    employmentPeriods,
    overtimePayouts,
    appSettings,
    allergyTags,
    personAllergyTags,
    studios,
    studioConfigs,
    studioConnections,
    studioConfigVersions,
    brokers,
    brokerOutputs,
    profiles,
    streams,
    streamAudioPairs,
    streamOutputs,
    streamBrokers,
    projectAwsResourceLinks,
    streamAudioFeeds,
    streamVideoFeeds,
    streamSignals,
    streamSignalAudioFeeds,
    streamTakers,
    streamTakerAudioPairs,
    streamTakerContacts,
    streamTakerAudioPairTests,
    defaultFunctionGroups,
    defaultFunctions,
    projectFunctionGroups,
    personSkillRatings,
    projectFunctionSeries,
    projectFunctions,
    functionAssignments,
    projectPersonTravelDays,
    tasks,
    taskAssignees,
    taskProjects,
    taskProjectGroups,
    notifications,
    comments,
    commentMentions,
    documents,
    studioDocuments,
    globalDocuments,
    globalDocumentLinks,
    meetingNoteProjectMentions,
    meetingNoteTags,
    meetingNotes,
    meetingNoteTagAssignments,
    meetingNoteMentions,
    quoteLineItemTemplates,
    quoteGroupTemplates,
    quoteGroupTemplateItems,
    clockifyTimeEntries,
    quotes,
    quoteVersions,
    quoteGroups,
    quoteLineItems,
    quoteDeductionItems,
    invoices,
    invoiceReminders,
    invoiceGroups,
    invoiceLineItems,
    invoiceDeductionItems,
];
export const projectRelationshipsList = [
    projectRelationships,
    availabilityRelationships,
    projectContactRelationships,
    crewEmailRelationships,
    crewEmailRecipientRelationships,
    employeeAbsenceRelationships,
    intercomAssignmentRelationships,
    personAllergyTagRelationships,
    studioRelationships,
    studioConnectionRelationships,
    studioConfigRelationships,
    brokerRelationships,
    streamRelationships,
    streamBrokerRelationships,
    projectAwsResourceLinkRelationships,
    streamSignalRelationships,
    streamSignalAudioFeedRelationships,
    streamTakerRelationships,
    streamTakerAudioPairRelationships,
    streamTakerContactRelationships,
    streamTakerAudioPairTestRelationships,
    projectGroupRelationships,
    peopleRelationships,
    skillRatingRelationships,
    defaultFunctionRelationships,
    projectFunctionSeriesRelationships,
    projectFunctionRelationships,
    projectFunctionAssignmentRelationships,
    projectPersonTravelDayRelationships,
    taskRelationships,
    taskAssigneeRelationships,
    taskProjectRelationships,
    taskProjectGroupRelationships,
    commentRelationships,
    commentMentionRelationships,
    meetingNoteRelationships,
    studioDocumentRelationships,
    globalDocumentRelationships,
    meetingNoteTagAssignmentRelationships,
    meetingNoteMentionRelationships,
    meetingNoteProjectMentionRelationships,
    quoteGroupTemplateRelationships,
    quoteGroupTemplateItemRelationships,
    clockifyTimeEntryRelationships,
    quoteRelationships,
    quoteVersionRelationships,
    quoteGroupRelationships,
    quoteLineItemRelationships,
    quoteDeductionItemRelationships,
    invoiceRelationships,
    invoiceReminderRelationships,
    invoiceGroupRelationships,
    invoiceLineItemRelationships,
    invoiceDeductionItemRelationships,
];
export const projectSchema = createSchema({
    tables: [...projectTables, peopleTable],
    relationships: projectRelationshipsList,
});
export const projectZql = createBuilder(projectSchema);
